import { SubscriberContainer, UniqueArrayBuffer } from '@benzinga/containers';
import Hooks from '@benzinga/hooks';
import { GridTransactionV2 } from '@benzinga/pro-ui-v2';
import { HoldingsQuote } from '@benzinga/quotes-v3-holdings-manager';
import { useWatchlistHoldingsQuotesCol } from '@benzinga/watchlist-manager-hooks/holdings';
import React from 'react';
import { Note } from '@benzinga/notes-manager';
import { StockSymbol } from '@benzinga/session';
import { TableParameters } from '@benzinga/ag-grid-utils';
import { GridApi } from 'ag-grid-community';
import { useBatchQuotesCallback } from '@benzinga/quotes-v3-manager-hooks';
import { Quote } from '@benzinga/quotes-v3-manager';
import { DEFAULT_WATCHLIST_CONFIG } from '../utils';
import { getTickerSlug } from '@benzinga/pro-ui';

interface DataProps {
  watchlistId: string;
  symbols: StockSymbol[];
  gridLayout: TableParameters;
  gridApi: React.MutableRefObject<GridApi<any> | null>;
}

type WatchlistGridHolder = HoldingsQuote & { notes?: { notes: Note[]; symbol: StockSymbol } };

interface WatchlistVisibleData {
  fields: string[];
  symbols: StockSymbol[];
}

const SYMBOL_SOCKET_LIMIT = 50;
const QUOTE_FLUSH_MS = 30;
const VIEWPORT_EVENTS = [
  'bodyScrollEnd',
  'displayedColumnsChanged',
  'firstDataRendered',
  'sortChanged',
  'filterChanged',
] as const;

const haveSameMembers = (a: string[], b: string[]) => {
  if (a.length !== b.length) return false;
  const members = new Set(a);
  return b.every(item => members.has(item));
};

const getRowSymbol = (gridApi: GridApi, index: number): StockSymbol | undefined => {
  const node = gridApi.getDisplayedRowAtIndex(index);
  return getTickerSlug(node) ?? undefined;
};

const getDisplayedFields = (gridApi: GridApi) => gridApi.getAllDisplayedColumns().map(column => column.getColId());

/**
 * Symbols to stream for the current viewport. Each change rebuilds the holdings feed and
 * its socket subscription, so the window is padded around the visible rows (up to the
 * socket limit) and kept while the visible rows stay inside it.
 */
const getSubscriptionWindow = (gridApi: GridApi, subscribed: StockSymbol[]): StockSymbol[] => {
  const first = gridApi.getFirstDisplayedRowIndex();
  const last = gridApi.getLastDisplayedRowIndex();
  if (first < 0 || last < first) return subscribed;

  const subscribedSet = new Set(subscribed);
  let covered = true;
  for (let i = first; i <= last; i++) {
    const symbol = getRowSymbol(gridApi, i);
    if (symbol && !subscribedSet.has(symbol)) {
      covered = false;
      break;
    }
  }
  if (covered) return subscribed;

  const visibleCount = last - first + 1;
  const padding = Math.max(0, Math.floor((SYMBOL_SOCKET_LIMIT - visibleCount) / 2));
  const start = Math.max(0, first - padding);
  const end = Math.min(gridApi.getDisplayedRowCount() - 1, last + padding);
  const window: StockSymbol[] = [];
  for (let i = start; i <= end; i++) {
    const symbol = getRowSymbol(gridApi, i);
    if (symbol) window.push(symbol);
  }
  return window.slice(0, SYMBOL_SOCKET_LIMIT);
};

/** Coalesces ticks per symbol and flushes them in one batch. */
const createQuoteBuffer = (onFlush: (quotes: HoldingsQuote[]) => void, delay: number) => {
  let pending = new Map<StockSymbol, HoldingsQuote>();
  let timer: ReturnType<typeof setTimeout> | undefined;

  const flush = () => {
    timer = undefined;
    const quotes = [...pending.values()];
    pending = new Map();
    if (quotes.length > 0) onFlush(quotes);
  };

  return {
    push: (quote: HoldingsQuote) => {
      pending.set(quote.symbol, { ...pending.get(quote.symbol), ...quote });
      timer ??= setTimeout(flush, delay);
    },
    clear: () => {
      clearTimeout(timer);
      timer = undefined;
      pending = new Map();
    },
  };
};

export const useDataHook = (props: DataProps) => {
  const [visibleData, setVisibleData] = React.useState<WatchlistVisibleData>(() => ({
    fields: DEFAULT_WATCHLIST_CONFIG.table.columns.map(c => c.colId),
    symbols: [],
  }));
  const [hasInitialQuotesLoaded, setHasInitialQuotesLoaded] = React.useState(false);
  const hasInitialQuotesLoadedRef = React.useRef(hasInitialQuotesLoaded);
  hasInitialQuotesLoadedRef.current = hasInitialQuotesLoaded;

  const [collection] = React.useState(
    () =>
      new SubscriberContainer<WatchlistGridHolder, UniqueArrayBuffer<WatchlistGridHolder>>(
        Infinity,
        new UniqueArrayBuffer<WatchlistGridHolder>((item: WatchlistGridHolder) => item.symbol),
      ),
  );

  const [quoteBuffer] = React.useState(() =>
    createQuoteBuffer(quotes => {
      const rows = new Map(collection.getBufferedItems().map(item => [item.symbol, item]));
      const updates = quotes.flatMap(quote => {
        const row = rows.get(quote.symbol);
        return row ? [{ ...row, ...quote }] : [];
      });
      if (updates.length > 0) collection.updateItems(updates);
    }, QUOTE_FLUSH_MS),
  );
  React.useEffect(() => quoteBuffer.clear, [quoteBuffer]);

  const updateMultipleTransaction = React.useCallback(
    (quotes: HoldingsQuote[]) => {
      const existingItems = collection.getBufferedItems();
      if (!hasInitialQuotesLoadedRef.current) {
        const newItems = [...existingItems];
        const indexBySymbol = new Map(newItems.map((item, index) => [item.symbol, index]));
        quotes.forEach(quote => {
          const index = indexBySymbol.get(quote.symbol);
          if (index !== undefined) {
            newItems[index] = { ...newItems[index], ...quote };
          } else {
            indexBySymbol.set(quote.symbol, newItems.length);
            newItems.push(quote);
          }
        });
        collection.replace(newItems);
        setHasInitialQuotesLoaded(true);
        return;
      }

      const rows = new Map(existingItems.map(item => [item.symbol, item]));
      collection.updateItems(quotes.map(quote => ({ ...rows.get(quote.symbol), ...quote })));
    },
    [collection],
  );

  const [transaction] = React.useState(() => new GridTransactionV2<HoldingsQuote>());
  Hooks.useSubscriber(collection, event => {
    if (event.type !== 'update') return;
    if (props.symbols.length === 0) {
      transaction.flush();
      collection.clear();
      props.gridApi.current?.setGridOption('rowData', []);
      return;
    }
    transaction.transaction(event);
  });

  const memoizedFields = React.useMemo(
    () => new Set<keyof HoldingsQuote>(visibleData.fields as Array<keyof HoldingsQuote>),
    [visibleData.fields],
  );
  useWatchlistHoldingsQuotesCol(props.watchlistId, memoizedFields, quoteBuffer.push, visibleData.symbols);

  // Fields fetched over REST. Grows when a never-fetched column becomes visible
  // (the socket only streams changes); refreshKey re-triggers the fetch.
  const [batch, setBatch] = React.useState(() => ({
    fields: new Set(props.gridLayout.columns.map(c => c.colId)) as Set<keyof Quote>,
    refreshKey: 0,
  }));

  const fetchMissingFields = React.useCallback((fields: string[]) => {
    setBatch(prev => {
      const missing = fields.filter(field => !prev.fields.has(field as keyof Quote));
      if (missing.length === 0) return prev;
      return {
        fields: new Set([...prev.fields, ...missing]) as Set<keyof Quote>,
        refreshKey: prev.refreshKey + 1,
      };
    });
  }, []);

  useBatchQuotesCallback(
    props.symbols,
    batch.fields,
    updateMultipleTransaction,
    hasInitialQuotesLoaded,
    undefined,
    batch.refreshKey,
  );

  // Watchlist switch: reset everything keyed to the old list.
  React.useEffect(() => {
    collection.replace([]);
    quoteBuffer.clear();
    setHasInitialQuotesLoaded(false);
    setVisibleData(prev => (prev.symbols.length > 0 ? { ...prev, symbols: [] } : prev));
  }, [collection, quoteBuffer, props.watchlistId]);

  // Keep the collection and streamed symbols in sync with props.symbols (adds and removals).
  React.useEffect(() => {
    const symbolsSet = new Set(props.symbols);
    const current = collection.getBufferedItems();
    const kept = current.filter(item => symbolsSet.has(item.symbol));
    const keptSymbols = new Set(kept.map(item => item.symbol));
    const added = props.symbols.filter(symbol => !keptSymbols.has(symbol));
    if (added.length === 0 && kept.length === current.length) return;

    collection.replace([...kept, ...added.map(symbol => ({ symbol }) as WatchlistGridHolder)]);
    setVisibleData(prev => {
      const stillStreaming = prev.symbols.filter(symbol => symbolsSet.has(symbol));
      // New symbols stream right away, capped at the socket limit.
      return { ...prev, symbols: [...new Set([...stillStreaming, ...added])].slice(0, SYMBOL_SOCKET_LIMIT) };
    });
  }, [props.watchlistId, props.symbols, collection]);

  const refreshVisibleData = React.useCallback(
    (gridApi: GridApi) => {
      const fields = getDisplayedFields(gridApi);
      fetchMissingFields(fields);
      setVisibleData(prev => {
        const nextFields = fields.length > 0 && !haveSameMembers(prev.fields, fields) ? fields : prev.fields;
        const windowSymbols = getSubscriptionWindow(gridApi, prev.symbols);
        const nextSymbols = haveSameMembers(prev.symbols, windowSymbols) ? prev.symbols : windowSymbols;
        return { fields: nextFields, symbols: nextSymbols };
      });
    },
    [fetchMissingFields],
  );

  // Grid api lives in state so listener setup/teardown is a plain effect.
  const [gridApi, setGridApi] = React.useState<GridApi | null>(null);
  React.useEffect(() => {
    if (!gridApi) return;
    const onViewportChanged = () => refreshVisibleData(gridApi);
    VIEWPORT_EVENTS.forEach(type => gridApi.addEventListener(type, onViewportChanged));
    onViewportChanged(); // don't depend on catching firstDataRendered
    return () => {
      if (gridApi.isDestroyed()) return;
      VIEWPORT_EVENTS.forEach(type => gridApi.removeEventListener(type, onViewportChanged));
    };
  }, [gridApi, refreshVisibleData]);

  return { onGridReady: setGridApi, transaction };
};